import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LayoutDashboard, CalendarClock, FileText, FileCheck2, Images, ClipboardCheck,
  MessageSquare, LogOut, ArrowLeft, Send, Plus, Download, Star, CheckCircle2, Clock, DollarSign, CreditCard } from "lucide-react";
import { LogoLockup, LogoMark } from "../components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startLogin = () => {
  const redirectUrl = window.location.origin + "/portal";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "visits", label: "Visits", icon: CalendarClock },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "contracts", label: "Contracts", icon: FileCheck2 },
  { key: "photos", label: "Photos", icon: Images },
  { key: "reports", label: "Reports", icon: ClipboardCheck },
  { key: "messages", label: "Messages", icon: MessageSquare },
];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

function LoginScreen({ error }) {
  return (
    <div data-testid="portal-login" className="min-h-screen bg-ink-950 glow relative flex items-center justify-center px-6">
      <Link to="/" className="absolute top-8 left-8 flex items-center gap-2 text-white/50 hover:text-white text-sm"><ArrowLeft className="h-4 w-4" /> Back to site</Link>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-md glass rounded-4xl p-10 text-center">
        <div className="mb-8 flex justify-center"><LogoLockup dark className="h-10" /></div>
        <p className="overline text-steel-light mb-2">Client Portal</p>
        <h1 className="font-serif text-gradient text-4xl mb-3" style={{ fontWeight: 500 }}>Welcome back.</h1>
        <p className="text-white/55 text-sm mb-8">Sign in to view your visits, invoices, reports, and before/after photos.</p>
        {error && <p className="text-red-300 text-sm mb-4">Sign-in failed. Please try again.</p>}
        <button data-testid="google-login-btn" onClick={startLogin}
          className="btn-pill w-full bg-white text-ink-950 py-4 text-sm font-600 flex items-center justify-center gap-3 hover:shadow-xl hover:shadow-white/20">
          <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
          Continue with Google
        </button>
        <p className="text-white/35 text-xs mt-6">Secure sign-in powered by Google.</p>
      </motion.div>
    </div>
  );
}

const Badge = ({ children, tone = "steel" }) => {
  const tones = { paid: "bg-green-400/15 text-green-300", due: "bg-amber-400/15 text-amber-300", scheduled: "bg-steel/25 text-steel-light", completed: "bg-white/10 text-white/60", active: "bg-green-400/15 text-green-300" };
  return <span className={`text-xs rounded-full px-2.5 py-1 font-600 capitalize ${tones[children] || tones[tone] || "bg-steel/25 text-steel-light"}`}>{children}</span>;
};

export default function ClientPortal() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [msg, setMsg] = useState("");

  const loadData = useCallback(async () => {
    try { const r = await ax.get("/portal/data"); setData(r.data); setLoadErr(false); }
    catch { setLoadErr(true); }
  }, []);

  useEffect(() => {
    if (location.state?.error) { setAuthed(false); return; }
    if (location.state?.user) { setAuthed(true); loadData(); return; }
    (async () => {
      try { const r = await ax.get("/portal/auth/me"); setUser(r.data); setAuthed(true); loadData(); }
      catch { setAuthed(false); }
    })();
  }, [location.state, loadData]);

  const logout = async () => { try { await ax.post("/portal/auth/logout"); } catch {} navigate("/"); };
  const sendMsg = async () => {
    if (!msg.trim()) return;
    const text = msg;
    try { await ax.post("/portal/messages", { text }); setMsg(""); toast.success("Message sent"); loadData(); }
    catch { toast.error("Could not send message. Try again."); }
  };
  const requestService = async (service) => {
    try { await ax.post("/portal/service-request", { service }); toast.success(`Requested: ${service}`); }
    catch { toast.error("Could not submit request. Try again."); }
  };
  const payInvoice = async (inv) => {
    try {
      const r = await ax.post("/payments/invoice-checkout", { invoice_number: inv.number, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start payment. Try again.");
    }
  };

  if (authed === null) return <div className="min-h-screen bg-ink-950 flex items-center justify-center"><LogoMark className="h-12 w-12 animate-pulse" color="#7FA8C9" /></div>;
  if (authed === false) return <LoginScreen error={location.state?.error} />;

  const k = data?.kpis || {};
  const kpiCards = [
    { icon: CalendarClock, label: "Next Visit", value: k.next_visit ? fmtDate(k.next_visit) : "—" },
    { icon: Clock, label: "Upcoming", value: k.upcoming_count ?? 0 },
    { icon: DollarSign, label: "Open Balance", value: `$${(k.open_balance ?? 0).toLocaleString()}` },
    { icon: Star, label: "Quality Score", value: `${k.quality_score ?? 0}%` },
  ];

  return (
    <div data-testid="client-portal" className="min-h-screen bg-ink-950 flex">
      <aside className="w-64 bg-ink-900 border-r border-white/8 hidden md:flex flex-col fixed h-full">
        <div className="p-6 border-b border-white/8"><Link to="/"><LogoLockup dark className="h-8" /></Link></div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.key} data-testid={`portal-tab-${n.key}`} onClick={() => setTab(n.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${tab === n.key ? "bg-white/10 text-white" : "text-white/55 hover:text-white hover:bg-white/5"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/8">
          <div className="flex items-center gap-3 px-3 py-2 mb-1">
            {user?.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" /> : <div className="h-8 w-8 rounded-full bg-steel/30" />}
            <div className="min-w-0"><div className="text-white text-sm truncate">{user?.name}</div><div className="text-white/40 text-xs truncate">{user?.email}</div></div>
          </div>
          <button data-testid="portal-logout" onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm text-white/55 hover:text-white hover:bg-white/5"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64 min-w-0">
        <header className="glass-strong border-b border-white/8 px-6 md:px-10 py-6 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="font-serif text-2xl text-white" style={{ fontWeight: 500 }}>{NAV.find((n) => n.key === tab)?.label}</h1>
            <p className="text-white/45 text-sm">Welcome back, {user?.name?.split(" ")[0] || "there"}.</p>
          </div>
          <button onClick={logout} className="md:hidden text-white"><LogOut className="h-5 w-5" /></button>
        </header>

        <div className="p-6 md:p-10">
          <div className="flex md:hidden gap-2 mb-5 overflow-x-auto">
            {NAV.map((n) => <button key={n.key} data-testid={`portal-mtab-${n.key}`} onClick={() => setTab(n.key)} className={`btn-pill px-4 py-2 text-xs whitespace-nowrap ${tab === n.key ? "bg-white text-ink-950" : "glass text-white/60"}`}>{n.label}</button>)}
          </div>

          {!data ? <div className="text-white/40 p-16 text-center" data-testid="portal-loading">{loadErr ? <>Couldn’t load your portal. <button onClick={loadData} className="text-steel-light underline ml-1">Retry</button></> : "Loading your portal…"}</div> : (
          <>
          {tab === "dashboard" && (
            <div className="space-y-6" data-testid="portal-dashboard">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {kpiCards.map((c) => (
                  <div key={c.label} className="glass rounded-3xl p-5">
                    <div className="h-9 w-9 rounded-xl bg-white/5 flex items-center justify-center mb-3"><c.icon className="h-4 w-4 text-steel-light" /></div>
                    <div className="font-serif text-2xl text-white" style={{ fontWeight: 500 }}>{c.value}</div>
                    <div className="text-white/45 text-xs mt-1">{c.label}</div>
                  </div>
                ))}
              </div>
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="glass rounded-3xl p-6 lg:col-span-2">
                  <p className="overline text-steel-light mb-4">Upcoming Visits</p>
                  <div className="space-y-3">
                    {data.visits.filter((v) => v.status === "scheduled").slice(0, 3).map((v, i) => (
                      <div key={i} className="flex items-center justify-between border-b border-white/8 pb-3">
                        <div><div className="text-white font-500">{v.service}</div><div className="text-white/45 text-sm">{fmtDate(v.date)} · {v.window}</div></div>
                        <Badge>{v.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="glass rounded-3xl p-6">
                  <p className="overline text-steel-light mb-4">Request Service</p>
                  <div className="space-y-2">
                    {["Extra Deep Clean", "Window Cleaning", "Emergency Response"].map((s) => (
                      <button key={s} data-testid={`request-${s}`} onClick={() => requestService(s)} className="w-full flex items-center justify-between glass rounded-2xl px-4 py-3 text-sm text-white/80 hover:bg-white/10 transition-colors">
                        {s} <Plus className="h-4 w-4 text-steel-light" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "visits" && (
            <div className="glass rounded-3xl divide-y divide-white/8" data-testid="portal-visits">
              {data.visits.map((v, i) => (
                <div key={i} className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {v.status === "completed" ? <CheckCircle2 className="h-5 w-5 text-green-300" /> : <Clock className="h-5 w-5 text-steel-light" />}
                    <div><div className="text-white font-500">{v.service}</div><div className="text-white/45 text-sm">{fmtDate(v.date)} · {v.window} · {v.crew}</div></div>
                  </div>
                  <Badge>{v.status}</Badge>
                </div>
              ))}
            </div>
          )}

          {tab === "invoices" && (
            <div className="glass rounded-3xl divide-y divide-white/8" data-testid="portal-invoices">
              {data.invoices.map((inv, i) => (
                <div key={i} className="p-5 flex items-center justify-between">
                  <div><div className="text-white font-500">{inv.number} <span className="text-white/40 font-400">· {inv.period}</span></div><div className="text-white/45 text-sm">{fmtDate(inv.date)}</div></div>
                  <div className="flex items-center gap-4">
                    <span className="font-serif text-xl text-white" style={{ fontWeight: 500 }}>${inv.amount.toLocaleString()}</span>
                    <Badge>{inv.status}</Badge>
                    {inv.status === "due" && (
                      <button data-testid={`pay-invoice-${inv.number}`} onClick={() => payInvoice(inv)}
                        className="btn-pill bg-white text-ink-950 px-4 py-2 text-xs font-600 flex items-center gap-1.5 hover:shadow-lg hover:shadow-white/20">
                        <CreditCard className="h-3.5 w-3.5" /> Pay Now
                      </button>
                    )}
                    <button onClick={() => window.print()} className="text-white/50 hover:text-white" title="Download"><Download className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "contracts" && (
            <div className="space-y-4" data-testid="portal-contracts">
              {data.contracts.map((c, i) => (
                <div key={i} className="glass rounded-3xl p-7 flex items-center justify-between">
                  <div><div className="text-white font-serif text-xl" style={{ fontWeight: 500 }}>{c.name}</div><div className="text-white/45 text-sm mt-1">{c.term} · Since {c.start} · {c.value}</div></div>
                  <div className="flex items-center gap-3"><Badge>{c.status}</Badge><button onClick={() => window.print()} className="text-white/50 hover:text-white"><Download className="h-4 w-4" /></button></div>
                </div>
              ))}
            </div>
          )}

          {tab === "photos" && (
            <div className="grid md:grid-cols-2 gap-6" data-testid="portal-photos">
              {data.photos.map((p, i) => (
                <div key={i} className="glass rounded-3xl p-5">
                  <div className="flex items-center justify-between mb-3"><span className="text-white font-500">{p.area}</span><span className="text-white/40 text-xs">{fmtDate(p.date)}</span></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><div className="text-white/40 text-xs mb-1.5 uppercase tracking-wide">Before</div><img src={p.before} alt="before" className="rounded-2xl aspect-[4/3] object-cover w-full" /></div>
                    <div><div className="text-steel-light text-xs mb-1.5 uppercase tracking-wide">After</div><img src={p.after} alt="after" className="rounded-2xl aspect-[4/3] object-cover w-full" /></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "reports" && (
            <div className="space-y-4" data-testid="portal-reports">
              {data.reports.map((r, i) => (
                <div key={i} className="glass rounded-3xl p-6 flex items-center justify-between">
                  <div><div className="text-white font-500">{r.title}</div><div className="text-white/45 text-sm">{fmtDate(r.date)} · Inspector {r.inspector}</div></div>
                  <div className="flex items-center gap-4">
                    <div className="text-right"><div className="font-serif text-2xl text-gradient" style={{ fontWeight: 500 }}>{r.score}%</div><div className="text-white/40 text-xs">Quality</div></div>
                    <button onClick={() => window.print()} className="text-white/50 hover:text-white"><Download className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "messages" && (
            <div className="glass rounded-3xl p-6 max-w-2xl" data-testid="portal-messages">
              <div className="space-y-4 mb-6 max-h-[50vh] overflow-y-auto">
                {data.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.team ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.team ? "glass text-white/80 rounded-tl-sm" : "bg-white text-ink-950 rounded-tr-sm"}`}>
                      {m.text}
                      <div className={`text-[10px] mt-1 ${m.team ? "text-white/40" : "text-ink-950/50"}`}>{m.from} · {fmtDate(m.at)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input data-testid="portal-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()}
                  placeholder="Message your account manager…" className="flex-1 bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-white/35 focus:outline-none focus:border-steel-light/60" />
                <button data-testid="portal-send" onClick={sendMsg} className="btn-pill bg-white text-ink-950 px-5 flex items-center"><Send className="h-4 w-4" /></button>
              </div>
            </div>
          )}
          </>
          )}
        </div>
      </main>
    </div>
  );
}
